import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPortalQueueCount } from '../api/client';
import MobileBottomTabBar from '../components/MobileBottomTabBar';
import MobileFAB from '../components/MobileFAB';
import { useAuth } from '../auth/AuthContext';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useIsMobile } from '../hooks/useIsMobile';
import { useFirmConfig } from '../contexts/FirmConfigContext';
import { TopBarContext, type TopBarAction } from './TopBarActionsContext';
import { AppDesktopSidebar, AppMobileDrawer } from './AppSidebar';
import AppTopBar from './AppTopBar';
import { AppLayoutOverlays } from './AppLayoutOverlays';
import { resolveDisplayUser } from './displayUser';

export default function AppLayout() {
  const firm = useFirmConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const showAdminBackLink = location.pathname.startsWith('/admin/') && location.pathname !== '/admin';
  const isMatterDetailRoute = /^\/matters\/\d+(?:\/|$)/.test(location.pathname);
  const suppressMobileFloatingActions = isMobile && isMatterDetailRoute;
  const mobileContentBottomPadding = suppressMobileFloatingActions
    ? 'var(--mobile-content-bottom-tabs)'
    : 'var(--mobile-content-bottom-actions)';
  const [topBarActions, setTopBarActions] = useState<TopBarAction[]>([]);
  const [mobileTaskAction, setMobileTaskActionState] = useState<(() => void) | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showCreateMatter, setShowCreateMatter] = useState(false);
  const [showCreateContact, setShowCreateContact] = useState(false);
  const [showQuickIntake, setShowQuickIntake] = useState(false);
  const [showQuickNote, setShowQuickNote] = useState(false);
  const [showPunchModal, setShowPunchModal] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const { authEnabled, user, logout } = useAuth();
  const { data: currentUser } = useCurrentUser();
  // The MSAL `user` (AccountInfo) is null for native (password) sessions, which left the
  // top-bar/sidebar logout control hidden for native logins. Fall back to the current app
  // user (works for both auth methods via the session cookie) so logout + name always show.
  const displayUser = resolveDisplayUser(user, currentUser);
  const isAdmin = currentUser?.role === 'admin' || (!authEnabled && !currentUser); // show admin nav in dev mode only
  const canViewKpis = isAdmin || currentUser?.role === 'intake_supervisor';

  const { data: portalQueueData } = useQuery({
    queryKey: ['portal-queue-count'],
    queryFn: getPortalQueueCount,
    refetchInterval: 60000,
  });
  const portalQueueCount = portalQueueData?.count || 0;

  // Clear top bar actions on route change + close mobile drawer
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setTopBarActions([]);
      if (!location.pathname.startsWith('/tasks')) {
        setMobileTaskActionState(null);
      }
      setDrawerOpen(false);
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [location.pathname]);

  const setMobileTaskAction = useCallback((action: (() => void) | null) => {
    setMobileTaskActionState(() => action);
  }, []);

  const handleMobileNewTask = useCallback(() => {
    if (location.pathname.startsWith('/tasks') && mobileTaskAction) {
      mobileTaskAction();
      return;
    }
    navigate('/ai-hub?compose=task');
  }, [location.pathname, mobileTaskAction, navigate]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (isMobile && drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, drawerOpen]);

  // Close sidebar menus on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <TopBarContext.Provider value={{ setActions: setTopBarActions, setMobileTaskAction }}>
      <div style={{ display: 'flex', height: '100dvh' }}>

        {isMobile && (
          <AppMobileDrawer
            authEnabled={authEnabled}
            canViewKpis={canViewKpis}
            currentUserRole={currentUser?.role}
            drawerOpen={drawerOpen}
            firm={firm}
            isAdmin={isAdmin}
            logout={logout}
            onCloseDrawer={() => setDrawerOpen(false)}
            portalQueueCount={portalQueueCount}
            user={displayUser}
          />
        )}

        {!isMobile && (
          <AppDesktopSidebar
            authEnabled={authEnabled}
            canViewKpis={canViewKpis}
            collapsed={collapsed}
            currentUserRole={currentUser?.role}
            firm={firm}
            isAdmin={isAdmin}
            logout={logout}
            moreMenuRef={moreMenuRef}
            portalQueueCount={portalQueueCount}
            setCollapsed={setCollapsed}
            setShowMoreMenu={setShowMoreMenu}
            showMoreMenu={showMoreMenu}
            user={displayUser}
          />
        )}
        {/* Main area */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <AppTopBar
            authEnabled={authEnabled}
            isMobile={isMobile}
            logout={logout}
            onCloseMoreMenu={() => setShowMoreMenu(false)}
            onCreateContact={() => setShowCreateContact(true)}
            onCreateMatter={() => setShowCreateMatter(true)}
            onPunchClock={() => setShowPunchModal(true)}
            onQuickIntake={() => setShowQuickIntake(true)}
            topBarActions={topBarActions}
            user={displayUser}
          />
          {/* Page content */}
          <main style={{
            flex: 1,
            background: 'var(--bg)',
            padding: isMobile ? '1rem' : '1.5rem',
            paddingBottom: isMobile ? mobileContentBottomPadding : undefined,
            scrollPaddingBottom: isMobile ? mobileContentBottomPadding : undefined,
            overflow: 'auto',
            overflowX: isMobile ? 'auto' : undefined,
          }}>
            {showAdminBackLink && (
              <div style={{ marginBottom: '0.75rem' }}>
                <Link
                  to="/admin"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    color: 'var(--primary)',
                    textDecoration: 'none',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                  }}
                >
                  <span aria-hidden="true">&larr;</span>
                  <span>Back to Admin Center</span>
                </Link>
              </div>
            )}
            <Outlet />
          </main>
        </div>

        {/* Mobile bottom tab bar + FAB */}
        {isMobile && (
          <>
            {!suppressMobileFloatingActions && (
              <MobileFAB
                onNewMatter={() => setShowCreateMatter(true)}
                onNewNote={() => setShowQuickNote(true)}
                onNewTask={handleMobileNewTask}
              />
            )}
            <MobileBottomTabBar onMorePress={() => setDrawerOpen(true)} />
          </>
        )}
      </div>

      <AppLayoutOverlays
        showCreateContact={showCreateContact}
        showCreateMatter={showCreateMatter}
        showPunchModal={showPunchModal}
        showQuickIntake={showQuickIntake}
        showQuickNote={showQuickNote}
        onCloseCreateContact={() => setShowCreateContact(false)}
        onCloseCreateMatter={() => setShowCreateMatter(false)}
        onClosePunchModal={() => setShowPunchModal(false)}
        onCloseQuickIntake={() => setShowQuickIntake(false)}
        onCloseQuickNote={() => setShowQuickNote(false)}
        suppressFloatingActions={suppressMobileFloatingActions}
      />

      {/* Inline animation keyframes */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </TopBarContext.Provider>
  );
}
